export class TableRowSorter {
    private table: HTMLElement;
    private handleClass: string;
    private callback: (sortOrder: string[]) => void;
    private draggedRow: HTMLTableRowElement | null = null;
    private placeholder: HTMLTableRowElement;
    private redLine: HTMLDivElement | null = null;
    private dragPreview: HTMLElement | null = null;
    private offsetX: number = 0;
    private offsetY: number = 0;


    /**
     * Initializes the TableRowSorter with a table element, drag handle class, and callback function.
     * @param table The table element containing the rows to sort.
     * @param handleClass The class name of the drag handle elements.
     * @param callback Function to call with the new sort order after dragging.
     */
    constructor(table: HTMLElement, handleClass: string, callback: (sortOrder: string[]) => void) {
        this.table = table;
        this.handleClass = handleClass;
        this.callback = callback;

        this.placeholder = document.createElement('tr');
        this.placeholder.className = 'sort-placeholder';

        this.redLine = document.createElement('div');
        this.redLine.className = 'drag-red-line';
        document.body.appendChild(this.redLine);
        this.redLine.style.display = 'none';

        this.updateHandles();

        // initial order is recorded without notifying: a callback here posted a sort request on every page load
        // data-original-sort is owned by setupAjaxTableSort ('|' separated, what RecordSort expects); do not overwrite it here

        this.table.addEventListener('dragstart', this.onDragStart.bind(this));
        this.table.addEventListener('dragover', this.onDragOver.bind(this));
        this.table.addEventListener('drop', this.onDrop.bind(this));
        this.table.addEventListener('dragleave', this.onDragLeave.bind(this));
        this.table.addEventListener('dragend', this.onDragEnd.bind(this));
    }

    /**
     * Updates the draggable attribute on all elements with the specified handle class.
     * Call this method if the table content changes dynamically.
     */
    public updateHandles(): void {
        const handles = this.table.querySelectorAll(`.${this.handleClass}`);
        handles.forEach(handle => {
            handle.setAttribute('draggable', 'true');
        });
    }

    private onDragStart(event: DragEvent): void {
        const handle = event.target as HTMLElement;
        if (handle.classList.contains(this.handleClass)) {
            this.draggedRow = handle.closest('tr');
            if (this.draggedRow) {
                event.dataTransfer!.setData('text/plain', 'dragging-row');
                event.dataTransfer!.effectAllowed = 'move';

                // Calculate and store the initial offset
                const rowRect = this.draggedRow.getBoundingClientRect();
                this.offsetX = event.clientX - rowRect.left;
                this.offsetY = event.clientY - rowRect.top;

                this.dragPreview = this.draggedRow.cloneNode(true) as HTMLElement;

                // --- NEW LOGIC FOR COPYING ALL COMPUTED STYLES ---
                this.copyComputedStyles(this.draggedRow, this.dragPreview);
                // --- END NEW LOGIC ---

                // Set the overall width of the drag preview to match the dragged row
                // This is still important as getComputedStyle might not account for all layout effects
                this.dragPreview.style.width = this.draggedRow.offsetWidth + 'px';


                this.dragPreview.style.position = 'absolute';
                this.dragPreview.style.opacity = '0.7'; // Still apply a slight opacity for drag feedback
                this.dragPreview.style.pointerEvents = 'none';
                this.dragPreview.style.zIndex = '1000';

                // Position the drag preview correctly using the calculated offset
                this.dragPreview.style.top = (event.clientY - this.offsetY) + window.scrollY + 'px';
                this.dragPreview.style.left = (event.clientX - this.offsetX) + window.scrollX + 'px';

                document.body.appendChild(this.dragPreview);

                event.dataTransfer!.setDragImage(new Image(), 0, 0);

                this.draggedRow.classList.add('dragging');
            }
        } else {
            event.preventDefault();
        }
    }

    private onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.dataTransfer!.dropEffect = 'move';

        const targetRow = (event.target as HTMLElement).closest('tr');

        // Update the position of the drag preview using the stored offset
        if (this.dragPreview) {
            this.dragPreview.style.top = (event.clientY - this.offsetY) + 'px';
            this.dragPreview.style.left = (event.clientX - this.offsetX) + 'px';
        }

        if (this.redLine) {
            this.redLine.style.display = 'none';
        }

        if (targetRow && targetRow !== this.draggedRow) {
            const tbody = this.table.querySelector('tbody');
            if (!tbody) return;

            if (targetRow.classList.contains('sort-placeholder')) {
                return;
            }

            const rect = targetRow.getBoundingClientRect();
            const midpoint = rect.top + (rect.height / 2);

            let insertBeforeTarget: HTMLTableRowElement | null = null;
            if (event.clientY < midpoint) {
                insertBeforeTarget = targetRow;
            } else {
                if (targetRow.nextSibling instanceof HTMLTableRowElement) {
                    insertBeforeTarget = targetRow.nextSibling as HTMLTableRowElement;
                } else {
                    insertBeforeTarget = null;
                }
            }

            if (this.placeholder.parentNode !== tbody) {
                tbody.appendChild(this.placeholder);
            }

            if (this.redLine) {
                this.redLine.style.display = 'block';
                this.redLine.style.width = targetRow.offsetWidth + 'px';
                this.redLine.style.left = rect.left + window.scrollX + 'px';

                if (event.clientY < midpoint) {
                    this.redLine.style.top = rect.top + window.scrollY + 'px';
                } else {
                    this.redLine.style.top = (rect.bottom + window.scrollY) + 'px';
                }
            }

            if (insertBeforeTarget) {
                tbody.insertBefore(this.placeholder, insertBeforeTarget);
            } else {
                tbody.appendChild(this.placeholder);
            }
            this.placeholder.remove();
        }
    }

    private onDrop(event: DragEvent): void {
        event.preventDefault();
        if (this.redLine) {
            this.redLine.style.display = 'none';
        }

        if (this.draggedRow) {
            const tbody = this.table.querySelector('tbody');
            if (!tbody) return;

            let dropTargetRow: HTMLTableRowElement | null = null;
            let insertBefore: HTMLTableRowElement | null = null;

            const rows = Array.from(tbody.children) as HTMLTableRowElement[];
            let foundDropPosition = false;

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                if (row === this.draggedRow) continue;

                const rect = row.getBoundingClientRect();
                const midpoint = rect.top + (rect.height / 2);

                if (event.clientY < midpoint) {
                    insertBefore = row;
                    foundDropPosition = true;
                    break;
                }
            }

            if (!foundDropPosition && rows.length > 0) {
                insertBefore = null;
            } else if (!foundDropPosition && rows.length === 0) {
                insertBefore = null;
            }

            if (insertBefore) {
                tbody.insertBefore(this.draggedRow, insertBefore);
            } else {
                tbody.appendChild(this.draggedRow);
            }

            this.draggedRow.classList.remove('dragging');
            this.collectSortOrder();
        }
    }

    private onDragLeave(event: DragEvent): void {
        if (this.redLine) {
            this.redLine.style.display = 'none';
        }
    }

    private onDragEnd(event: DragEvent): void {
        if (this.dragPreview) {
            this.dragPreview.remove();
            this.dragPreview = null;
        }
        if (this.redLine) {
            this.redLine.style.display = 'none';
        }
        if (this.draggedRow) {
            this.draggedRow.classList.remove('dragging');
        }
        if (this.placeholder.parentNode) {
            this.placeholder.remove();
        }
        this.draggedRow = null;
        this.offsetX = 0;
        this.offsetY = 0;
    }

    private collectSortOrder(notify: boolean = true): string[] {
        const rows = this.table.querySelectorAll('tbody tr');
        const sortOrder: string[] = [];
        rows.forEach(row => {
            const key = row.getAttribute('data-ztable-sort-primary-key-id');
            if (key) {
                sortOrder.push(key);
            }
        });
        if (notify) this.callback(sortOrder);
        return sortOrder;
    }

    /**
     * Recursively copies computed styles from an original element and its children
     * to a cloned element and its children.
     * @param originalEl The original DOM element.
     * @param clonedEl The cloned DOM element.
     */
    private copyComputedStyles(originalEl: HTMLElement, clonedEl: HTMLElement): void {
        const computedStyle = window.getComputedStyle(originalEl);

        // Properties to copy directly. You can expand this list.
        // It's generally better to copy specific relevant properties rather than all of them.
        const propertiesToCopy = [
            'width', 'minWidth', 'maxWidth', 'height', 'minHeight', 'maxHeight',
            'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
            'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
            'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
            'borderColor', 'borderTopColor', 'borderRightColor', 'borderBottomColor', 'borderLeftColor',
            'borderStyle', 'borderTopStyle', 'borderRightStyle', 'borderBottomStyle', 'borderLeftStyle',
            'borderWidth', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
            'backgroundColor', 'color', 'fontSize', 'fontFamily', 'fontWeight',
            'textAlign', 'verticalAlign', 'boxSizing', 'display', 'lineHeight',
            'flex', 'flexGrow', 'flexShrink', 'flexBasis', 'justifyContent', 'alignItems', 'gap'
            // Add any other properties that are crucial for your table's appearance
        ];

        propertiesToCopy.forEach(prop => {
            const value = computedStyle.getPropertyValue(prop);
            if (value) {
                try { // Use try-catch for robustness, as some properties might be read-only or invalid on certain elements
                    (clonedEl.style as any)[prop] = value;
                } catch (e) {
                    // console.warn(`Could not set style property ${prop} on element`, clonedEl, e);
                }
            }
        });

        // Recursively apply to children
        const originalChildren = Array.from(originalEl.children) as HTMLElement[];
        const clonedChildren = Array.from(clonedEl.children) as HTMLElement[];

        originalChildren.forEach((originalChild, index) => {
            if (clonedChildren[index]) {
                this.copyComputedStyles(originalChild, clonedChildren[index] as HTMLElement);
            }
        });
    }
}